import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import CloseConfirmationModal, { useCloseConfirmationModal } from '../CloseConfirmationModal'
import Modal from './Modal'
import ModalSidebarContext from './ModalSidebarContext'
import useModalSteps from './useModalSteps'

const propTypes = {
  /**
   * If true, modal is shown.
   */
  show: PropTypes.bool,
  /**
   * Callback to hide the modal
   */
  onHide: PropTypes.func.isRequired,
  /**
   * Size of the modal
   */
  size: PropTypes.oneOf(['lg', 'md']),
  /**
   * If true, puts modal behind the backdrop, in case of another modal is opened on top of it for instance
   */
  toBackdrop: PropTypes.bool,
  /**
   * Function to determine when to show or not the close confirmation modal
   */
  shouldShowCloseConfirmation: PropTypes.func,
  /**
   * Modal content/panels
   */
  children: PropTypes.node.isRequired,
  /**
   * Steps array: each step should contain a label (node) and a key (string).
   * In case the modal has multiple panels, each panel should have a key indentifier defined
   * If the label is passed, display it on the header steps
   */
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      key: PropTypes.string,
    }),
  ),
  /**
   * Step where modal will be opened
   */
  initialStepKey: PropTypes.string,
  /**
   * Title of the modal
   * If a string is passed it needs to be a FormattedMessage id
   * If a react node is passed render it the way it is
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * If present, will render a pill with the label passed. FormattedMessage id
   */
  titlePillLabel: PropTypes.string,
  /**
   * Subtitles of the modal
   */
  subTitles: PropTypes.arrayOf(PropTypes.node),
  /**
   * Color variant of the header steps
   */
  variant: PropTypes.oneOf(['primary', 'engage']),
  /**
   * If true, show loading indicator instead of content
   */
  isLoading: PropTypes.bool,
  /**
   * Class to override default styles in the body
   */
  bodyClassName: PropTypes.string,
  /**
   * Description to display when loading. FormattedMessage id
   */
  loadingDescription: PropTypes.string,
  /**
   * Label of the HEADER back button. FormattedMessage id
   */
  headerBackButtonLabel: PropTypes.string,
  /**
   * Function triggered on the HEADER back button click
   */
  headerBackButtonAction: PropTypes.func,
}

/**
 * Modal sidebar with close confirmation that provides data (through context) for its children components
 * Covers usual use cases of multi panel modals on the app
 * If you don't want all the features you can always import the individual (Modal, Body, Header, Footer) from this folder
 */
function ModalSidebar(props) {
  const {
    show = false,
    onHide,
    size = 'lg',
    children,
    shouldShowCloseConfirmation = () => false,
    steps = [],
    initialStepKey = null,
    toBackdrop = false,
    title,
    subTitles = [],
    variant = 'primary',
    isLoading = false,
    bodyClassName = '',
    loadingDescription = null,
    titlePillLabel = null,
    headerBackButtonLabel = null,
    headerBackButtonAction = null,
  } = props ?? {}

  const [showValidationErrorBanner, setShowValidationErrorBanner] = useState(false)
  const [contextToBackdrop, setContextToBackdrop] = useState(false)
  const [isStillProcessing, setIsStillProcessing] = useState(false)

  // A request still processing after polling stopped leaves nothing unsaved, so closing doesn't need confirming
  const shouldConfirmClose = useCallback(
    () => !isStillProcessing && shouldShowCloseConfirmation(),
    [isStillProcessing, shouldShowCloseConfirmation],
  )

  const {
    showCloseConfirmationModal,
    hideCloseConfirmationModal,
    onHideOverride: onHideWithConfirmation,
  } = useCloseConfirmationModal({
    onHide,
    shouldShowCloseConfirmation: shouldConfirmClose,
  })

  const { currentStepIndex, currentStepKey, nextStep, previousStep, goToStep } = useModalSteps({
    steps,
    initialStepKey,
  })

  const provided = useMemo(
    () => ({
      onHideWithConfirmation,
      steps,
      currentStepIndex,
      currentStepKey,
      nextStep,
      previousStep,
      goToStep,
      showValidationErrorBanner,
      setShowValidationErrorBanner,
      setToBackdrop: setContextToBackdrop,
      isStillProcessing,
      setIsStillProcessing,
      title,
      subTitles,
      variant,
      isLoading,
      bodyClassName,
      loadingDescription,
      titlePillLabel,
      headerBackButtonLabel,
      headerBackButtonAction,
    }),
    [
      onHideWithConfirmation,
      showValidationErrorBanner,
      steps,
      currentStepIndex,
      currentStepKey,
      nextStep,
      previousStep,
      goToStep,
      isStillProcessing,
      title,
      subTitles,
      variant,
      isLoading,
      bodyClassName,
      loadingDescription,
      titlePillLabel,
      headerBackButtonLabel,
      headerBackButtonAction,
    ],
  )

  return (
    <>
      <Modal
        size={size}
        show={show}
        onHide={onHideWithConfirmation}
        toBackdrop={showCloseConfirmationModal || toBackdrop || contextToBackdrop}
      >
        <ModalSidebarContext.Provider value={provided}>{children}</ModalSidebarContext.Provider>
      </Modal>
      <CloseConfirmationModal
        show={showCloseConfirmationModal}
        onHideConfirmation={hideCloseConfirmationModal}
        onHideParentModal={onHide}
      />
    </>
  )
}

ModalSidebar.propTypes = propTypes

export default ModalSidebar
